import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { useTrialDashboardDesktopTypographyStyles } from '../../shared/useTrialDashboardDesktopTypographyStyles'
import { useStatCardSharedStyles } from '../shared/useStatCardSharedStyles'

const desktopPadding = '16px'
const desktopResponsivePadding = 'clamp(16px, 2vw, 32px)'

const useLayoutStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(6),
    minHeight: 'calc(100vh - 200px)',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: '50px',
    paddingRight: '50px',
    position: 'relative',
    overflow: 'visible',
    width: '100%',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(4),
      minHeight: 'calc(100vh - 200px)'
    },
    [theme.breakpoints.up('lg')]: {
      minHeight: 'calc(100vh - 200px)',
      gap: theme.spacing(6)
    }
  },
  whiteBackground: {
    position: 'absolute',
    width: 'calc(100% + 64px)',
    maxWidth: 'none',
    height: 'auto',
    minHeight: '347.452px',
    flexShrink: 0,
    border: '1.19px solid #D9DEE8',
    backgroundColor: '#FFFFFF',
    zIndex: 0,
    top: '50%',
    left: 'calc(50% - 32px)',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: theme.shape.borderRadius,
    display: 'block',
    visibility: 'visible',
    opacity: 1,
    pointerEvents: 'none',
    paddingTop: 0,
    paddingRight: desktopPadding,
    paddingBottom: 0,
    paddingLeft: desktopPadding,
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      width: 'calc(100vw - 64px)',
      maxWidth: '832.074px',
      minHeight: '303.083px'
    },
    '@media (min-width: 768px) and (max-width: 1024px)': {
      minHeight: '380px'
    },
    '@media (min-width: 958px) and (max-width: 1213px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '380px',
      top: 'calc(50% - 120px)',
      transform: 'translate(-50%, -50%)'
    },
    '@media (min-width: 600px) and (max-width: 789px)': {
      minHeight: '350px'
    },
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100% + 64px)',
      maxWidth: 'none',
      left: 'calc(50% - 32px)',
      transform: 'translate(-50%, -50%)',
      paddingTop: 0,
      paddingRight: desktopPadding,
      paddingBottom: 0,
      paddingLeft: desktopPadding,
      overflow: 'visible',
      minHeight: 'clamp(320px, calc(50vh - 200px), 400px)'
    },
    '@media (min-width: 1214px)': {
      paddingRight: desktopResponsivePadding,
      paddingLeft: desktopResponsivePadding,
      minHeight: 'clamp(300px, calc(45vh - 200px), 380px)'
    },
    [theme.breakpoints.up('xl')]: {
      width: 'calc(100% + 64px)',
      maxWidth: 'none',
      left: 'calc(50% - 32px)',
      transform: 'translate(-50%, -50%)',
      borderRadius: theme.shape.borderRadius,
      paddingTop: 0,
      paddingRight: desktopResponsivePadding,
      paddingBottom: 0,
      paddingLeft: desktopResponsivePadding,
      overflow: 'visible',
      minHeight: 'clamp(280px, calc(40vh - 200px), 360px)'
    },
    '@media (min-width: 1850px)': {
      minHeight: '340px'
    }
  },
  leftSection: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 2,
    maxWidth: '690.926px',
    padding: 0,
    marginLeft: 0,
    flexShrink: 1,
    minWidth: 0,
    width: 'auto',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%'
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 'clamp(300px, 38vw, 550px)',
      flexShrink: 1,
      width: 'auto'
    },
    '@media (min-width: 1214px)': {
      maxWidth: 'clamp(300px, 35vw, 500px)'
    },
    '@media (min-width: 1850px)': {
      maxWidth: '690.926px',
      flexShrink: 1
    }
  },
  leftSectionContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingTop: '50px',
    boxSizing: 'border-box',
    [theme.breakpoints.up('lg')]: {
      paddingTop: '50px',
      paddingLeft: 0,
      paddingRight: 0
    },
    '@media (min-width: 1214px)': {
      paddingLeft: 0,
      paddingRight: 0
    }
  },
  rightSection: {
    flex: '0 1 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'relative',
    zIndex: 2,
    flexShrink: 1,
    minWidth: 0,
    width: 'auto',
    maxWidth: '650px',
    overflow: 'visible',
    '@media (min-width: 768px) and (max-width: 1023px)': {
      marginTop: theme.spacing(8),
      maxWidth: '100%',
      width: '100%'
    },
    '@media (min-width: 958px) and (max-width: 1213px)': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '100%',
      maxWidth: '100%',
      marginTop: 0,
      boxSizing: 'border-box',
      alignItems: 'center',
      justifyContent: 'center'
    },
    [theme.breakpoints.up('lg')]: {
      paddingRight: 0,
      boxSizing: 'border-box',
      alignItems: 'center',
      justifyContent: 'flex-end',
      maxWidth: 'clamp(500px, 40vw, 650px)',
      width: 'auto'
    },
    '@media (min-width: 1214px)': {
      paddingRight: 0,
      maxWidth: 'clamp(500px, 40vw, 650px)'
    },
    [theme.breakpoints.up('xl')]: {
      paddingRight: 0,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      maxWidth: 'clamp(500px, 40vw, 650px)'
    }
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: 600,
    flexShrink: 0,
    overflow: 'visible',
    [theme.breakpoints.up('lg')]: {
      maxWidth: 'clamp(520px, 100%, 680px)',
      flexShrink: 0,
      width: '100%',
      margin: '0 auto',
      marginTop: 'calc(50px + 2.15rem + 16px - 102.417px)',
      boxSizing: 'border-box',
      gap: 'clamp(8px, 1.5vw, 16px)',
      overflow: 'visible'
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 'clamp(560px, 100%, 720px)',
      flexShrink: 0,
      width: '100%',
      margin: '0 auto',
      marginTop: 'calc(50px + 2.15rem + 16px - 102.417px)',
      boxSizing: 'border-box',
      gap: 'clamp(8px, 1.5vw, 16px)',
      overflow: 'visible'
    },
    '@media (min-width: 1850px)': {
      maxWidth: 680,
      marginTop: 'calc(50px + 2.69rem + 16px - 102.417px)',
      gap: theme.spacing(2),
      overflow: 'visible'
    }
  },
  statCard: {
    width: '100%',
    maxWidth: '293.07px',
    height: 'auto',
    minHeight: '204.834px',
    padding: 0,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    aspectRatio: '293.07 / 204.834',
    [theme.breakpoints.up('lg')]: {
      width: '100%',
      maxWidth: 'clamp(220px, 20vw, 293.07px)',
      height: 'auto',
      minHeight: 'clamp(154px, 13.6vw, 204.834px)',
      aspectRatio: '293.07 / 204.834'
    },
    [theme.breakpoints.up('xl')]: {
      width: '100%',
      maxWidth: 'clamp(240px, 22vw, 293.07px)',
      height: 'auto',
      minHeight: 'clamp(168px, 15.3vw, 204.834px)',
      aspectRatio: '293.07 / 204.834'
    },
    '@media (min-width: 1850px)': {
      width: '293.07px',
      maxWidth: '293.07px',
      height: '204.834px',
      minHeight: '204.834px',
      aspectRatio: 'auto'
    }
  }
}))

export const useTrialOverDashboardDesktopLayoutStyles = () => {
  const layoutStyles = useLayoutStyles()
  const typographyStyles = useTrialDashboardDesktopTypographyStyles()
  const sharedStyles = useStatCardSharedStyles()
  return { ...layoutStyles, ...typographyStyles, ...sharedStyles }
}
